if (!customElements.get('tgs-quiz')) {
  customElements.define('tgs-quiz', class TgsQuiz extends HTMLElement {
    constructor() {
      super();
      this.state = { step: -1, picked: [], result: null };
    }

    connectedCallback() {
      this.questions = this.readJson('[data-tgs-questions]') || [];
      this.answers   = this.readJson('[data-tgs-answers]')   || [];
      this.results   = this.readJson('[data-tgs-results]')   || [];
      this.voice     = this.readJson('[data-tgs-voice]')     || [];

      this.loadingMs = parseInt(this.dataset.loadingMs, 10) || 2200;
      this.loadingPhases = [
        this.dataset.loadingPhase1 || 'Reading your answers…',
        this.dataset.loadingPhase2 || 'Matching your formula…',
        this.dataset.loadingPhase3 || 'Found it.'
      ];

      this.elProgress      = this.querySelector('[data-progress]');
      this.elProgressFill  = this.querySelector('[data-progress-fill]');
      this.elProgressLabel = this.querySelector('[data-progress-label]');
      this.elQuestionMount = this.querySelector('[data-question-mount]');
      this.elNext          = this.querySelector('[data-next]');
      this.elBack          = this.querySelector('[data-back]');
      this.elLoadingMsg    = this.querySelector('[data-loading-message]');
      this.elResultMount   = this.querySelector('[data-result-mount]');

      this.bind();
      this.show('intro');
    }

    readJson(selector) {
      const node = this.querySelector(selector);
      if (!node) return null;
      try { return JSON.parse(node.textContent); }
      catch (e) { console.error('[tgs-quiz] bad JSON in', selector, e); return null; }
    }

    bind() {
      this.addEventListener('click', (e) => {
        const start  = e.target.closest('[data-start]');
        const back   = e.target.closest('[data-back]');
        const next   = e.target.closest('[data-next]');
        const retake = e.target.closest('[data-retake]');
        const cta    = e.target.closest('[data-result-cta]');
        const ans    = e.target.closest('[data-answer-button]');

        if (start)  { e.preventDefault(); this.start(); }
        if (back)   { e.preventDefault(); this.back(); }
        if (next)   { e.preventDefault(); this.advance(); }
        if (retake) { e.preventDefault(); this.reset(); }
        if (cta)    { this.emit('cta', { url: cta.href, key: this.state.result?.key }); }
        if (ans && !start && !back && !next) { this.toggleAnswer(ans); }
      });
    }

    start() {
      this.state.step = 0;
      this.state.picked = [];
      this.emit('start');
      this.renderQuestion();
    }

    reset() {
      this.state = { step: -1, picked: [], result: null };
      this.show('intro');
    }

    back() {
      if (this.state.step <= 0) return;
      this.state.step--;
      this.renderQuestion();
    }

    advance() {
      const q = this.questions[this.state.step];
      if (!q) return;
      const selected = this.getSelectedForCurrent();
      if (selected.length === 0) return;
      if (q.multi && selected.length === 0) return;

      this.state.picked[this.state.step] = selected;
      this.emit('step', { step: this.state.step + 1, questionId: q.id, answers: selected.map(a => a.label) });
      this.state.step++;

      if (this.state.step >= this.questions.length) {
        this.finalize();
      } else {
        this.renderQuestion();
      }
    }

    toggleAnswer(btn) {
      const q = this.questions[this.state.step];
      if (!q) return;
      const isSelected = btn.getAttribute('data-selected') === 'true';

      if (q.multi) {
        btn.setAttribute('data-selected', isSelected ? 'false' : 'true');
        btn.setAttribute('aria-pressed', isSelected ? 'false' : 'true');
        this.updateNextButton();
      } else {
        // Single select: clear siblings, mark this, then auto-advance after a beat.
        this.elQuestionMount.querySelectorAll('[data-answer-button]').forEach(b => {
          b.setAttribute('data-selected', 'false');
          b.setAttribute('aria-pressed', 'false');
        });
        btn.setAttribute('data-selected', 'true');
        btn.setAttribute('aria-pressed', 'true');
        setTimeout(() => this.advance(), 260);
      }
    }

    getSelectedForCurrent() {
      const q = this.questions[this.state.step];
      const buttons = this.elQuestionMount.querySelectorAll('[data-answer-button][data-selected="true"]');
      const out = [];
      buttons.forEach(b => {
        const ans = this.answers.find(a => a.qid === q.id && a.label === b.dataset.answerLabel);
        if (ans) out.push(ans);
      });
      return out;
    }

    updateNextButton() {
      const q = this.questions[this.state.step];
      const selected = this.getSelectedForCurrent();
      if (q.multi) {
        this.elNext.hidden = false;
        this.elNext.setAttribute('aria-disabled', selected.length === 0 ? 'true' : 'false');
      } else {
        this.elNext.hidden = true;
      }
    }

    renderQuestion() {
      this.show('question');
      this.elProgress.hidden = false;

      const q = this.questions[this.state.step];
      const total = this.questions.length;
      const pct = ((this.state.step + 1) / total) * 100;
      this.elProgressFill.style.width = `${pct}%`;
      this.elProgressLabel.textContent = `Step ${this.state.step + 1} of ${total}`;

      const previousPicks = this.state.picked[this.state.step] || [];
      const answerHtml = this.answers
        .filter(a => a.qid === q.id)
        .map(a => {
          const sel = previousPicks.some(p => p.label === a.label) ? 'true' : 'false';
          const icon = a.icon
            ? `<span class="tgs-quiz__answer-icon"><img src="${a.icon}" alt="" loading="lazy"></span>`
            : '';
          return `
            <li>
              <button type="button"
                class="tgs-quiz__answer"
                data-answer-button
                data-answer-label="${this.escape(a.label)}"
                data-selected="${sel}"
                aria-pressed="${sel}">
                ${icon}
                <span class="tgs-quiz__answer-label">${this.escape(a.label)}</span>
                <span class="tgs-quiz__answer-check" aria-hidden="true"></span>
              </button>
            </li>`;
        })
        .join('');

      this.elQuestionMount.innerHTML = `
        <h2 class="tgs-quiz__question-text">${this.escape(q.text)}</h2>
        ${q.subtext ? `<p class="tgs-quiz__question-subtext">${this.escape(q.subtext)}</p>` : ''}
        <ul class="tgs-quiz__answers" role="${q.multi ? 'group' : 'radiogroup'}">${answerHtml}</ul>
      `;

      this.elBack.hidden = this.state.step === 0;
      this.updateNextButton();
    }

    finalize() {
      this.show('loading');
      this.elProgressFill.style.width = '100%';
      this.elProgressLabel.textContent = `Step ${this.questions.length} of ${this.questions.length}`;

      const phaseDuration = this.loadingMs / this.loadingPhases.length;
      this.loadingPhases.forEach((msg, i) => {
        setTimeout(() => {
          if (!this.elLoadingMsg) return;
          this.elLoadingMsg.dataset.fading = 'true';
          setTimeout(() => {
            this.elLoadingMsg.textContent = msg;
            this.elLoadingMsg.dataset.fading = 'false';
          }, 180);
        }, i * phaseDuration);
      });

      setTimeout(() => {
        const key = this.score();
        const result = this.results.find(r => r.key === key) || this.results[0];
        this.state.result = result;
        this.renderResult(result);
        this.show('result');
        this.elProgress.hidden = true;
        this.emit('complete', { key, result, picked: this.state.picked });
      }, this.loadingMs);
    }

    score() {
      const tally = {
        format: { pouch: 0, sticks: 0 },
        flavor: { vanilla: 0, mocha: 0, original: 0 }
      };
      this.state.picked.flat().forEach(a => {
        if (a.score_format && a.score_format !== 'neutral' && tally.format[a.score_format] !== undefined) {
          tally.format[a.score_format]++;
        }
        if (a.score_flavor && a.score_flavor !== 'neutral' && tally.flavor[a.score_flavor] !== undefined) {
          tally.flavor[a.score_flavor]++;
        }
      });

      const format = tally.format.sticks > tally.format.pouch ? 'sticks' : 'pouch';
      const flavorEntries = Object.entries(tally.flavor)
        .sort((a, b) => b[1] - a[1] || (a[0] === 'vanilla' ? -1 : 1));
      const flavor = flavorEntries[0][1] > 0 ? flavorEntries[0][0] : 'vanilla';

      return `${format}_${flavor}`;
    }

    renderResult(result) {
      if (!result) {
        this.elResultMount.innerHTML = '<p>We couldn\'t match a product. Please retake the quiz.</p>';
        return;
      }

      const voiceKeys = this.state.picked.flat()
        .map(a => a.voice_key)
        .filter(Boolean);
      const dynamicBullets = voiceKeys
        .map(k => this.voice.find(v => v.key === k))
        .filter(Boolean)
        .slice(0, 2)
        .map(v => `<li>${v.text}</li>`)
        .join('');

      const fallbackBullets = result.why_bullets || '';
      const bulletsHtml = dynamicBullets
        ? `<ul class="tgs-quiz__why">${dynamicBullets}</ul>`
        : (fallbackBullets ? `<div class="tgs-quiz__why-fallback">${fallbackBullets}</div>` : '');

      const imgHtml = result.image
        ? `<div class="tgs-quiz__result-image"><img src="${result.image}" alt="${this.escape(result.title || '')}" loading="lazy"></div>`
        : '';

      const priceHtml = result.price
        ? `<div class="tgs-quiz__result-price">${result.price}</div>`
        : '';

      this.elResultMount.innerHTML = `
        <div class="tgs-quiz__result-eyebrow">Your match</div>
        <h2 class="tgs-quiz__result-headline">${this.escape(result.headline || result.title || 'The Good Stuff')}</h2>
        ${result.subhead ? `<p class="tgs-quiz__result-subhead">${this.escape(result.subhead)}</p>` : ''}
        <div class="tgs-quiz__result-card">
          ${imgHtml}
          <div class="tgs-quiz__result-body">
            <h3 class="tgs-quiz__result-title">${this.escape(result.title || '')}</h3>
            ${priceHtml}
            ${bulletsHtml}
            <a class="tgs-quiz__button tgs-quiz__result-cta" href="${result.url}" data-result-cta>
              ${this.escape(result.cta_text || 'Shop The Good Stuff')}
            </a>
          </div>
        </div>
      `;
    }

    show(stageName) {
      this.querySelectorAll('[data-stage]').forEach(s => {
        s.hidden = s.dataset.stage !== stageName;
      });
      if (stageName === 'intro' || stageName === 'loading' || stageName === 'result') {
        // Progress only shown during questions
        if (stageName !== 'loading') this.elProgress.hidden = true;
      }
      // Scroll the quiz into view at the top on stage transitions.
      if (stageName !== 'intro') {
        const top = this.getBoundingClientRect().top + window.pageYOffset - 24;
        if (window.scrollY > top + 120) window.scrollTo({ top, behavior: 'smooth' });
      }
    }

    escape(s) {
      if (s == null) return '';
      return String(s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    emit(name, detail = {}) {
      this.dispatchEvent(new CustomEvent(`tgs-quiz:${name}`, { detail, bubbles: true }));
    }
  });
}
